import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import useNotifications from '@/composables/useNotifications'
import i18n from '@/libs/i18n'
import useCommon from '@/views/organization/useCommon'
import { useLambda } from '@/views/habit/awsLambda'
import { oid } from '@/helpers/mongoHelpers'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'
import { hasPermission } from '@/composables/usePermission'

export default function useRoleList() {
  const { client = null, role = null } = store.state?.userStore?.userData
  const { showErrorMessage } = useNotifications()
  const { handleError } = useCommon()
  const { updateItem } = useLambda()
  const { rolesQueryWithOffset } = useMongoDBFunctions()
  const refRoleListTable = ref(null)
  const roles = ref([])

  // Table Handlers
  const tableColumns = computed(() => {
    return [
      { key: 'Actions', label: i18n.t('message.tableHeader.actions') },
      { key: 'code', label: i18n.t('message.tableHeader.code'), sortable: true },
      { key: 'name', label: i18n.t('message.tableHeader.name'), sortable: true },
      { key: 'description', label: i18n.t('message.tableHeader.description'), sortable: true },
      // { key: 'responsibilities', sortable: false },
      // { key: 'qualifications', sortable: false },
    ]
  })

  const totalRoles = ref(0)
  const perPage = ref(10)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const sortBy = ref('code')
  const isSortDirDesc = ref(false)
  const statusFilter = ref(null)
  const isLoading = ref(true)

  const dataMeta = computed(() => {
    const localItemsCount = refRoleListTable.value ? refRoleListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalRoles.value,
    }
  })

  const index = async () => {
    isLoading.value = true

    try {
      const input = {
        limit: perPage.value,
        total: currentPage.value === 1,
        offset: (currentPage.value * perPage.value) - perPage.value,
        query: {},
        search: searchQuery.value,
        sortBy: sortBy.value,
        sortOrder: isSortDirDesc.value ? -1 : 1,
        client_id: (role !== 'admin') ? client.$oid : null,
      }

      const { roles: w, totalRecords: total } = await rolesQueryWithOffset(input)
    
      roles.value = w
      if (currentPage.value === 1) totalRoles.value = total
    } catch (error) {
      console.log(error)
      handleError({ error, defaultMessage: i18n.t('message.role_list_fetch_error') })
    } finally {
      isLoading.value = false
    }
  }

  const deleteRole = async (id, callback) => {
    if (!hasPermission('organization-roles', 'delete')) {
      showErrorMessage(i18n.t('message.you_are_not_authorized'))
      callback(false)
      return
    }

    try {
      const query = { _id: oid(id), client_id: oid(client?.$oid) }
      const payload = { deleted: true }
      const action = { $set: payload }

      await updateItem({ collection: 'role', query, action })

      callback(true)
    } catch (error) {
      console.log(error)
      showErrorMessage(i18n.t('message.role_remove_error'))
      callback(false)
    }
  }

  const showingMessage = computed(() => {
    const from = ((currentPage.value * perPage.value) - perPage.value) + (roles.value.length ? 1 : 0)
    const to = roles.value.length + ((currentPage.value * perPage.value) - perPage.value)

    return i18n.tc('message.paginationText', 0, { from: from, to: to, total: totalRoles.value })
  })

  watch([currentPage, perPage, statusFilter], () => {
    index()
  })

  watch([searchQuery, sortBy, isSortDirDesc], () => {
    currentPage.value = 1
    index()
  })

  return {
    index,
    tableColumns,
    perPage,
    currentPage,
    totalRoles,
    showingMessage,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    roles,
    refRoleListTable,
    deleteRole,
    isLoading,
    role,
  }
}
